import React, { useState, useEffect } from "react";
import "./resetPassword.scss";
import logo from "../../assets/images/townOf1000Trails.svg";
import { Card, CardBody, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import HookForm from "../../components/hookForm/hookForm";
import TextField from "../../components/textField/textField";
import Constant from "../../util/constant";
import CustomButton from "../../components/customButton/customButton";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../store/features/toast/toastSlice";
import { useDispatch } from "react-redux";
import axios from "axios";
import { RESET_PASSWORD } from "../../api/endPoint";
import { Helmet } from "react-helmet";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showPassConfirm, setShowPassConfirm] = useState(false);
  const [formMethod, setFormMethod] = useState<any>(null);

  useEffect(() => {
    const email = localStorage.getItem("forgotPasswordEmail");
    const otp = localStorage.getItem("OTP");
    if (!email || !otp) {
      navigate("/404"); // Redirect to a 404 page if no email or OTP is found
    }
  }, [navigate]);

  const resetPasswordForm = {
    newPassword: {
      name: "newPassword",
      validate: {
        required: {
          value: true,
        },
        validate: {
          hasUppercase: (value: string) =>
            (value && value.match(Constant.REGEX.UPPERCASE) !== null) ||
            "Minimum 1 uppercase character",
          hasLowerCase: (value: string) =>
            (value && value.match(Constant.REGEX.LOWERCASE) !== null) ||
            "Minimum 1 lowercase character",
          hasNumbers: (value: string) =>
            (value && value.match(Constant.REGEX.NUMBER) !== null) ||
            "Minimum 1 numeric character",
          hasSpecialChar: (value: string) =>
            (value && value.match(Constant.REGEX.SPECIALCHARACTERS) !== null) ||
            "Minimum 1 special character",
          length: (value: string) =>
            (value && value.length >= 8 && value.length <= 16) ||
            "Minimum 8 - Maximum 16 characters long",
          noEmoji: (value: string) =>
            (value && !value.match(Constant.REGEX.EMOJI)) ||
            "Password cannot contain emojis",
        },
      },
    },
    confirmPassword: {
      name: "confirmPassword",
    },
  };

  const onFormSubmit = async (formData: any) => {
    setLoading(true);
    const email = localStorage.getItem("forgotPasswordEmail"); // Retrieve email from local storage

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}${RESET_PASSWORD}`,
        {
          email: email,
          password: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        }
      );

      // Handle success response
      dispatch(
        showToast({
          show: true,
          message: response.data.message || "Your password has been updated.",
          type: "success",
        })
      );

      setTimeout(() => {
        setLoading(false);
        navigate("/");
      }, 2000); // 2 seconds delay
    } catch (error: any) {
      // Handle error
      dispatch(
        showToast({
          show: true,
          message:
            error.response?.data?.message ||
            "Failed to reset password. Please try again.",
          type: "error",
        })
      );
      setLoading(false);

      // Clear password fields on error
      formMethod.reset({
        newPassword: "",
        confirmPassword: "",
      });
    }
  };

  const multiErrorFields = [
    { length: t("PASSWORD_MUST_BE_BETWEEN_8_AND_16_CHARACTERS") },
    { hasSpecialChar: t("MINIMUM_1_SPECIAL_CHARACTER") },
    { hasNumbers: t("MINIMUM_1_NUMERIC_CHARACTER") },
    { hasUppercase: t("MINIMUM_1_UPPERCASE_CHARACTER") },
    { hasLowerCase: t("MINIMUM_1_LOWERCASE_CHARACTER") },
    { noEmoji: t("PASSWORD_CANNOT_CONTAIN_EMOJIS") },
  ];

  const handleNewPasswordChange = (value: string) => {
    const trimmedValue = value.trim(); // Trim spaces

    if (typeof window !== "undefined") {
      localStorage.setItem("newPassword", trimmedValue); // Save email as a plain string
    }
  };
  const handleConfirmPasswordChange = (value: string) => {
    const trimmedValue = value.trim(); // Trim spaces
    if (typeof window !== "undefined") {
      localStorage.setItem("confirmPassword", trimmedValue); // Save email as a plain string
    }
  };

  const getInitialFormData = () => ({
    newPassword: localStorage.getItem("newPassword") || "",
    confirmPassword: localStorage.getItem("confirmPassword") || "",
  });
  return (
    <>
      <Helmet>
        <title>Reset Password | Town of 1000 Trails Admin Panel</title>
        <meta
          name="description"
          content="Log in to manage in-app trails and more."
        />
      </Helmet>
      <div className="reset-container">
        <div>
          <img className="pt-50 pb-30 logo" src={logo} alt="logo" />
          <Card className="overflow-hidden cardStyle" color="white">
            <CardBody className="d-flex  bg-indigo justify-content-between p-24 ">
              <div className="d-flex flex-column">
                <h4 className="text-left text-primary">
                  {t("RESET_PASSWORD")}
                </h4>
                <span className="title text-primary">
                  {t("PLEASE_ENTER_YOUR_NEW_PASSWORD")}
                </span>
              </div>
              <div className="iconStyle">
                <i className="icon icon-key"></i>
              </div>
            </CardBody>
            <CardBody className="text-center px-24 py-32">
              <HookForm
                defaultForm={getInitialFormData()}
                onSubmit={async (formData) => {
                  onFormSubmit(formData);
                }}
                init={(method) => setFormMethod(method)}
              >
                {(formMethod) => {
                  return (
                    <>
                      <div className="resetPassWrapper mb-4">
                        <div className="texFeildWrapper mb-3">
                          <div className="d-flex">
                            <Label className="lable">{t("NEW_PASSWORD")}</Label>
                            <span className="redStar mx-2">*</span>
                          </div>
                          <div className="passWrapper">
                            <TextField
                              formMethod={formMethod}
                              rules={resetPasswordForm.newPassword.validate}
                              name={resetPasswordForm.newPassword.name}
                              multiErrorFields={multiErrorFields}
                              errors={formMethod?.formState.errors}
                              maxLength={16}
                              passwordType={true}
                              disabled={loading}
                              type={showPass ? "text" : "password"}
                              placeholder={t("ENTER_NEW_PASSWORD")}
                              onChange={(value) => {
                                formMethod?.watch("newPassword") &&
                                  formMethod.trigger("newPassword");

                                const trimmedValue = value.target.value?.trim(); // Trim spaces
                                formMethod.setValue(
                                  resetPasswordForm.newPassword.name,
                                  trimmedValue
                                ); // Update form state with trimmed value
                                handleNewPasswordChange(trimmedValue); // Optionally call your handler to manage the state if needed
                              }}
                              triggerValidation={(fieldName) => {
                                if (fieldName === "newPassword") {
                                  formMethod.trigger("confirmPassword"); // Revalidate confirmPassword when newPassword changes
                                }
                              }}
                            />
                            <i
                              onClick={() => setShowPass(!showPass)}
                              className={
                                showPass
                                  ? "icon icon-eye-fill"
                                  : "icon icon-eye-slash-fill"
                              }
                            ></i>
                          </div>
                        </div>

                        <div className="texFeildWrapper mb-3">
                          <div className="d-flex">
                            <Label className="lable">
                              {t("CONFIRM_PASSWORD")}
                            </Label>
                            <span className="redStar mx-2">*</span>
                          </div>
                          <div className="passWrapper">
                            <TextField
                              formMethod={formMethod}
                              rules={{
                                required: true,
                                validate: {
                                  matchPassword: (value: string) =>
                                    value?.trim() ===
                                      formMethod
                                        ?.watch("newPassword")
                                        ?.trim() ||
                                    t("BOTH_PASSWORD_MUST_BE_MATCH"),
                                },
                              }}
                              name={resetPasswordForm.confirmPassword.name}
                              errors={formMethod?.formState.errors}
                              passwordType={true}
                              type={showPassConfirm ? "text" : "password"}
                              placeholder={t("CONFIRM_PASSWORD")}
                              disabled={loading}
                              onChange={(e) => {
                                formMethod?.watch("confirmPassword") &&
                                  formMethod.trigger("confirmPassword");

                                const trimmedValue = e.target.value?.trim(); // Trim spaces
                                formMethod.setValue(
                                  resetPasswordForm.confirmPassword.name,
                                  trimmedValue
                                ); // Update form state with trimmed value
                                handleConfirmPasswordChange(trimmedValue); // Optionally call your handler to manage the state if needed
                              }}
                            />
                            <i
                              onClick={() =>
                                setShowPassConfirm(!showPassConfirm)
                              }
                              className={
                                showPassConfirm
                                  ? "icon icon-eye-fill"
                                  : "icon icon-eye-slash-fill"
                              }
                            ></i>
                          </div>
                        </div>
                      </div>
                      <div className="submitDiv">
                        <CustomButton
                          className="btn btn-danger text-white btn-lg w-100 text-center justify-content-center"
                          type="submit"
                          title={t("RESET")}
                          loading={loading}
                          disabled={!formMethod?.formState.isValid}
                        />
                      </div>
                      <div>
                        <CustomButton
                          color="primary"
                          onClick={() => navigate("/")}
                          className="btn btn-transparent d-flex justify-content-center align-items-center mt-3 w-100 p-0"
                          title=""
                        >
                          <i className="me-2 icon icon-chevron-left"></i>{" "}
                          {t("BACK_TO_LOGIN")}
                        </CustomButton>
                      </div>
                    </>
                  );
                }}
              </HookForm>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
